// The coastal page briefing's safety policy v2 inputs (design 6.2, P2C-WP9-04, WP-S0 handover to
// the coastal page). The page passes them only for the sports CONDITIONS_SAFETY_POLICY_V2_SPORTS
// lists, so with the flag unset every briefing input, and so every briefing, is today's.
import type { NormalisedHour } from "@/lib/conditions/daily-obs-types";
import type { CoastalBriefingInput } from "@/lib/conditions/local-briefing";

/**
 * Whether the storm evidence behind the page's `stormActive` exists: that check reads CAPE and
 * lifted index on daylight hours, so the forecast is known only when every daylight hour has both.
 * No daylight hour means no storm evidence at all, so it is not known.
 */
export function stormForecastKnown(hours: readonly NormalisedHour[] | null): boolean {
  const daylight = (hours ?? []).filter((hour) => hour.weather.is_day);
  return daylight.length > 0 &&
    daylight.every((hour) => hour.weather.cape_jkg != null && hour.weather.lifted_index != null);
}

/** The gust at an hour, or null when the provider had none (the normaliser fills a gap with 0). */
export function hourGustKmh(hour: NormalisedHour | null): number | null {
  if (!hour || hour.source_presence?.weather.windGust === false) return null;
  const gust = hour.weather.wind_gust_10m_kmh;
  return typeof gust === "number" && Number.isFinite(gust) ? gust : null;
}

export type BriefingSafetyV2Fields = Pick<
  CoastalBriefingInput,
  "safetyPolicyV2" | "windSpeedKnown" | "stormForecastKnown" | "windGustKmh"
>;

/**
 * The v2 fields of one sport's briefing input: none unless the flag lists the sport, so an
 * unlisted sport's input keeps exactly today's keys.
 */
export function briefingSafetyV2Fields(
  sport: string,
  listedSports: readonly string[],
  evidence: Readonly<{ windSpeedKmh: number | null | undefined; hours: readonly NormalisedHour[] | null; currentHour: NormalisedHour | null }>,
): BriefingSafetyV2Fields {
  if (!listedSports.includes(sport)) return {};
  return {
    safetyPolicyV2: true,
    windSpeedKnown: evidence.windSpeedKmh != null,
    stormForecastKnown: stormForecastKnown(evidence.hours),
    windGustKmh: hourGustKmh(evidence.currentHour),
  };
}
